import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { swatchTextColor } from '@peruselab/structure-editor/domain/chemicalPalette';
import { Icon } from '../../components/Icon';
import { useAuth } from '../../auth/useAuth';
import { accountSettingsUrl } from '../../auth/authClient';
import { useThemePreference } from '../../theme/useThemePreference';
import { THEME_ACCENT_OPTIONS, THEME_MODE_OPTIONS } from '../../theme/themePreference';
import { useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { useDismissableOverlay } from '../../hooks/useDismissableOverlay';

// #512/#516 positioned this family of popovers with `position: fixed` via useAnchoredPopover
// instead of plain CSS `position: absolute` -- this one was missed in that pass (#525). 340px
// matches `.workspace-account-menu`'s width, the estimate needed before the menu is
// first mounted and can be measured for real.
const ACCOUNT_MENU_ESTIMATED_WIDTH_PX = 340;

// ADR 0007 P7 — the `👤` slot in the top bar. A button opening a small menu with the
// signed-in identity and Sign out. `useAuth` is also called in `App.tsx`; calling it again
// here just adds another subscriber to the shared `userManager`, which is cheaper than
// threading `auth` through `AppRouter` into the shell.
export function AccountMenu() {
  const { t } = useTranslation();
  const { user, signOut, addAuthenticator, signInError } = useAuth();
  const location = useLocation();
  const { mode, accent, setMode, setAccent } = useThemePreference();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const modeLabelId = useId();
  const accentLabelId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const anchor = useAnchoredPopover(open, triggerRef, menuRef, () => ACCOUNT_MENU_ESTIMATED_WIDTH_PX);
  useDismissableOverlay(open, setOpen, triggerRef, menuRef);

  const profile = user?.profile;
  const identity =
    (typeof profile?.name === 'string' && profile.name) ||
    (typeof profile?.preferred_username === 'string' && profile.preferred_username) ||
    (typeof profile?.email === 'string' && profile.email) ||
    profile?.sub ||
    '';

  return (
    <div className="workspace-account">
      <button
        ref={triggerRef}
        type="button"
        className="icon-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t('workspace.account.menuLabel')}
        title={t('workspace.account.menuLabel')}
        onClick={() => setOpen((v) => !v)}
        data-testid="account-menu-trigger"
      >
        <Icon name="accountCircle" />
      </button>
      {open && anchor ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t('workspace.account.menuLabel')}
          className="workspace-account-menu"
          style={{ top: anchor.top, left: anchor.left, maxHeight: `calc(100dvh - ${anchor.top + 8}px)` }}
          data-testid="account-menu"
        >
          {identity ? (
            <p className="workspace-account-identity">
              <span>{t('workspace.account.signedInAs')}</span>
              <strong>{identity}</strong>
            </p>
          ) : null}

          {/* #502 (ADR 0012) -- the app's first theme control. menuitemradio/aria-checked, not
              plain menuitem: these are a "choose one" selection, not one-shot commands like Sign
              out below (the W3C APG's menu-containing-a-radio-group composite pattern). */}
          <div className="workspace-account-theme">
            <div className="workspace-account-theme-heading">
              <Icon name="palette" size="1.1rem" />
              <span>{t('workspace.account.theme.heading')}</span>
            </div>
            <div id={modeLabelId} className="workspace-account-theme-label">
              {t('workspace.account.theme.modeLabel')}
            </div>
            <div className="workspace-account-theme-modes" role="group" aria-labelledby={modeLabelId}>
              {THEME_MODE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={mode === option.value}
                  className={mode === option.value ? 'active' : ''}
                  onClick={() => setMode(option.value)}
                >
                  {t(option.labelKey)}
                </button>
              ))}
            </div>

            <div id={accentLabelId} className="workspace-account-theme-label">
              {t('workspace.account.theme.accentLabel')}
            </div>
            <div
              className="workspace-account-theme-accents chemical-swatch-grid"
              role="group"
              aria-labelledby={accentLabelId}
            >
              {THEME_ACCENT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={accent === option.value}
                  aria-label={option.name}
                  title={option.pigment}
                  className={`chemical-swatch ${accent === option.value ? 'active' : ''}`}
                  style={{ backgroundColor: option.swatch, color: swatchTextColor(option.swatch) }}
                  onClick={() => setAccent(option.value)}
                >
                  <span>{option.name}</span>
                  {accent === option.value ? <Icon name="check" size="1rem" /> : null}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            role="menuitem"
            className="workspace-account-link"
            onClick={() => addAuthenticator(`${location.pathname}${location.search}${location.hash}`)}
          >
            <Icon name="lock" size="1.1rem" />
            <span>{t('workspace.account.addAuthenticator')}</span>
          </button>
          {/* #1043: manage credentials, authenticator removal, and active sessions via Keycloak Account Console */}
          <a
            href={accountSettingsUrl()}
            role="menuitem"
            className="workspace-account-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name="settings" size="1.1rem" />
            <span>{t('workspace.account.manageCredentials')}</span>
          </a>
          {signInError ? (
            <p role="alert" className="import-error">
              {signInError}
            </p>
          ) : null}
          {/* #957: the user manual, a static page beside the app. */}
          <a href="/help/" role="menuitem" className="workspace-account-link">
            <Icon name="info" size="1.1rem" />
            <span>{t('help.link')}</span>
          </a>
          {/* ADR 0016 (#895): your own comments, wherever they are. */}
          <Link to="/me/comments" role="menuitem" className="workspace-account-link" onClick={() => setOpen(false)}>
            <Icon name="forum" size="1.1rem" />
            <span>{t('discussions.myComments.menu')}</span>
          </Link>
          {/* #1051: your organisations, their members and settings. */}
          <Link
            to="/me/organisations"
            role="menuitem"
            className="workspace-account-link"
            onClick={() => setOpen(false)}
          >
            <Icon name="corporateFare" size="1.1rem" />
            <span>{t('tenancy.page.heading')}</span>
          </Link>
          <button type="button" role="menuitem" className="workspace-account-signout" onClick={() => signOut()}>
            <Icon name="logout" size="1.1rem" />
            <span>{t('workspace.account.signOut')}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
